import React, { useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/router';

import Image from 'components/uiLibrary/Image';
import LinkButton, {
  PrimaryButton,
  QuaternaryButton,
  SecondaryButton,
  TextButton,
} from 'components/uiLibrary/LinkButton';
import Typography from 'components/uiLibrary/Typography';

import EntityName from 'components/Globals/EntityName';

import { OUTBOUND_LINK_TYPES, RESPONSE_STATUS_CODES } from 'constants/index';

import { getShuffledArray } from 'utils/globals';
import { sentryCaptureMessage } from 'utils/globals/sentry';
import useAppContext from 'utils/hooks/useAppContext';
import usePageContext from 'utils/hooks/usePageContext';

import { ENTITY_TYPES, ROUTE_VALIDITY } from 'operabase-router/constants';

import LogoImage from 'public/images/logo.png';

import PageWrapper from '../PageWrapper';

import classes from './PageError.module.scss';

const CODE_CONTENT = {
  [RESPONSE_STATUS_CODES.PERMANENT_REDIRECT]: {
    message: 'Moved Permanently!',
    content: 'The URL requested has been moved permanently.',
  },
  [RESPONSE_STATUS_CODES.TEMPORARY_REDIRECT]: {
    message: 'Temporary Redirect!',
    content: 'The URL requested has been moved temporarily.',
  },
  [RESPONSE_STATUS_CODES.BAD_REQUEST]: {
    message: 'Bad Request!',
    content: 'You have used invalid syntax.',
  },
  [RESPONSE_STATUS_CODES.UNAUTHORIZED]: {
    message: 'Unauthorized!',
    content: 'The URL requested requires authorization.',
  },
  [RESPONSE_STATUS_CODES.FORBIDDEN]: {
    message: 'Forbidden!',
    content: 'You are not permitted to access the requested URL.',
  },
  [RESPONSE_STATUS_CODES.NOT_FOUND]: {
    message: 'Not Found!',
    content: 'The requested URL was not found on this server.',
  },
  [RESPONSE_STATUS_CODES.METHOD_NOT_ALLOWED]: {
    message: 'Method Not Allowed!',
    content: 'The method used is not permitted.',
  },
  [RESPONSE_STATUS_CODES.INTERNAL_SERVER_ERROR]: {
    message: 'Internal Server Error!',
    content: 'The requested URL caused an error on the server.',
  },
  [RESPONSE_STATUS_CODES.BAD_GATEWAY]: {
    message: 'Bad Gateway!',
    content: 'This server received an invalid response from an upstream server it accessed to fulfill the request.',
  },
  [RESPONSE_STATUS_CODES.SERVICE_UNAVAILABLE]: {
    message: 'Service Unavailable!',
    content: 'The Service is not available at the moment due to a temporary overloading or maintenance of the server.',
  },
  UNKNOWN: {
    message: 'Page Error!',
    content: 'The requested URL caused an error on the page.',
  },
};

const ContextualActions = () => {
  const { navigate } = usePageContext();
  const { obRouteContext } = useAppContext();
  const { entityType, entity } = obRouteContext?.page || {};
  const entityMapValues = useMemo(() => {
    const items = Object.values(obRouteContext?.entityMap || {}).flat();

    if (items?.length > 10) {
      return getShuffledArray(items, 10);
    }

    return items;
  }, [obRouteContext?.entityMap]);

  const { pageLinkProps, otherEntityLinkProps } = useMemo(() => {
    let primaryLinkProps = null;

    if (entityType && entity?.id) {
      primaryLinkProps = navigate.getLinkProps({
        entityType,
        entity,
      });
    }

    const otherPageEntities = entityMapValues.reduce((acc, item) => {
      if (
        [
          ENTITY_TYPES.ORGANIZATION,
          ENTITY_TYPES.PROFILE,
          ENTITY_TYPES.COUNTRY,
          ENTITY_TYPES.WORK_TYPE,
          ENTITY_TYPES.WORK,
        ].includes(item.entityType) &&
        item?.entityType !== entityType &&
        item?.entity?.id !== entity?.id
      ) {
        acc.push({
          entity: item?.entity,
          entityType: item?.entityType,
          linkProps: navigate.getLinkProps({
            entityType: item?.entityType,
            entity: item?.entity,
          }),
        });
      }

      return acc;
    }, []);

    return {
      pageLinkProps: primaryLinkProps,
      otherEntityLinkProps: otherPageEntities,
    };
  }, [entityType, entity, navigate, entityMapValues]);

  return (
    <>
      {pageLinkProps && (
        <PrimaryButton size="small" shape="rectangle" {...pageLinkProps} title={entity?.name} isLink>
          <EntityName entity={entity} entityType={entityType} />
        </PrimaryButton>
      )}
      {otherEntityLinkProps?.map(item => (
        <SecondaryButton size="small" shape="rectangle" {...item?.linkProps} title={item?.entity?.name} isLink>
          <EntityName entity={item?.entity} entityType={item?.entityType} />
        </SecondaryButton>
      ))}
    </>
  );
};

const ErrorContent = ({ statusCode, errorId }) => {
  const { obRouteContext } = useAppContext();

  const content = CODE_CONTENT[statusCode] || CODE_CONTENT.UNKNOWN;
  const hasRouteContext = useMemo(() => Object.keys(obRouteContext).length > 0, [obRouteContext]);
  const pageURL = useMemo(
    () => (typeof window === 'undefined' ? obRouteContext?.url : window.location.href),
    [obRouteContext],
  );
  const reasons = useMemo(() => {
    const showDebugInfo = hasRouteContext && process.env.APP_ENV && process.env.APP_ENV !== 'production';

    if (showDebugInfo) {
      return obRouteContext?.paths
        ?.reduce((acc, path) => {
          if (path.validity === ROUTE_VALIDITY.VALID) {
            return acc;
          }

          return [...acc, `${path.path} (${path.reason})`];
        }, [])
        .join(', ');
    }

    return null;
  }, [hasRouteContext, obRouteContext]);

  return (
    <div className={classes.root}>
      <LinkButton variant="native" href="/[[..action]]" as="/" isLink>
        <Image
          src={LogoImage}
          alt="Operabase Home"
          lazy={false}
          className={classes.logo}
          disableCloudinary
          disableNextImage
        />
      </LinkButton>
      <div className={classes.content}>
        <Typography variant="h1" weight="medium" size="18">
          {content?.message} - {statusCode}
        </Typography>
        <Typography variant="h2" weight="medium" size="14">
          {content?.content}
        </Typography>
        <Typography variant="p" weight="regular" size="12">
          Please contact us at{' '}
          <TextButton
            shape="rectangle"
            href="mailto:contact@operabase.com"
            isLink
            external
            externalLinkType={OUTBOUND_LINK_TYPES.EMAIL}
          >
            contact@operabase.com
          </TextButton>
        </Typography>
      </div>
      <div className={classes.actions}>
        <div className={classes.actions__btns}>
          {statusCode !== RESPONSE_STATUS_CODES.INTERNAL_SERVER_ERROR && <ContextualActions />}
        </div>
        <div className={classes.actions__btns}>
          <QuaternaryButton size="small" shape="rectangle" href="/[[..action]]" as="/" isLink>
            Operabase Home
          </QuaternaryButton>
        </div>
      </div>
      <div className={classes.debug}>
        <Typography variant="p" weight="regular" size="10">
          <Typography weight="bold" size="10">
            URL:{' '}
          </Typography>
          <Typography weight="regular" size="10">
            {pageURL}
          </Typography>
        </Typography>
        <Typography variant="p" weight="regular" size="10">
          <Typography weight="bold" size="10">
            BUILD:{' '}
          </Typography>
          <Typography weight="regular" size="10">
            {process.env.BUILD_NUMBER}
          </Typography>
        </Typography>
        {hasRouteContext && (
          <>
            <Typography variant="p" weight="regular" size="10">
              <Typography weight="bold" size="10">
                TOKEN:{' '}
              </Typography>
              <Typography weight="regular" size="10">
                {obRouteContext.tokenized}
              </Typography>
            </Typography>
            <Typography variant="p" weight="regular" size="10">
              <Typography weight="bold" size="10">
                STATUS:{' '}
              </Typography>
              <Typography weight="regular" size="10">
                {obRouteContext.status}
              </Typography>
            </Typography>
            {obRouteContext.redirect && (
              <Typography variant="p" weight="regular" size="10">
                <Typography weight="bold" size="10">
                  REDIRECT:{' '}
                </Typography>
                <Typography weight="regular" size="10">
                  {obRouteContext.redirect}
                </Typography>
              </Typography>
            )}
          </>
        )}
        {reasons && (
          <Typography variant="p" weight="regular" size="10">
            <Typography weight="bold" size="10">
              REASONS:{' '}
            </Typography>
            <Typography weight="regular" size="10">
              {reasons}
            </Typography>
          </Typography>
        )}
        {errorId && (
          <Typography variant="p" weight="regular" size="10">
            <Typography weight="bold" size="10">
              ERROR ID:{' '}
            </Typography>
            <Typography weight="regular" size="10">
              {errorId}
            </Typography>
          </Typography>
        )}
        <Typography variant="p" weight="regular" size="10">
          <Typography weight="bold" size="10">
            TIMESTAMP:{' '}
          </Typography>
          <Typography weight="regular" size="10">
            {new Date().toISOString()}
          </Typography>
        </Typography>
      </div>
    </div>
  );
};

const PageError = ({ statusCode, error }) => {
  const [errorId, setErrorId] = useState(null);
  const { obRouteContext } = useAppContext();
  const router = useRouter();
  const content = CODE_CONTENT[statusCode] || CODE_CONTENT.UNKNOWN;

  const canRedirect = useMemo(
    () =>
      [RESPONSE_STATUS_CODES.PERMANENT_REDIRECT, RESPONSE_STATUS_CODES.TEMPORARY_REDIRECT].includes(statusCode) &&
      obRouteContext?.redirect,
    [obRouteContext, statusCode],
  );

  useEffect(() => {
    if (!errorId) {
      const {
        breadcrumbs: _breadcrumbs,
        entityMap: _entityMap,
        h1Title: _h1Title,
        linkProps: _linkProps,
        mainPath: _mainPath,
        tokenized: _tokenized,
        ...restObRouteContext
      } = obRouteContext || {};

      const sentryErrorId = sentryCaptureMessage(content?.message, {
        extra: {
          error,
          statusCode,
          canRedirect,
          ...restObRouteContext,
        },
      });

      if (sentryErrorId) {
        setErrorId(sentryErrorId);
      }
    }
  }, [content, obRouteContext, statusCode, error, errorId, canRedirect]);

  useEffect(() => {
    if (canRedirect) {
      router.replace(obRouteContext.redirect);
    }
  }, [canRedirect, router, obRouteContext?.redirect]);

  if (canRedirect) {
    return null;
  }

  if (statusCode === RESPONSE_STATUS_CODES.INTERNAL_SERVER_ERROR) {
    return <ErrorContent statusCode={statusCode} errorId={errorId} />;
  }

  return (
    <PageWrapper skipModals>
      <ErrorContent statusCode={statusCode} errorId={errorId} />
    </PageWrapper>
  );
};

export default PageError;
